import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useEffect, useState } from 'react'
import {
  useCustomers,
  useOrders,
  useOutlets,
  useOutstandingInvoices,
  usePaymentSchedule,
  useRecordPayment,
  useUpdatePayment,
} from '@/api/hooks'
import type { OrderDetail, Payment } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { ErrorBanner, formatDate, formatMobile, formatMoney, humanise, isoDate, newId } from '@/components/common'

const MODES = ['CASH', 'UPI', 'CARD', 'BANK_TRANSFER', 'CHEQUE', 'GATEWAY']

/**
 * Records a receipt.
 *
 * <p>The order matters: a payment carrying an order settles that order's
 * milestones and, when it covers the booking advance, confirms the order
 * outright. A payment without one only sits on the customer's account.</p>
 *
 * <p>Given an existing receipt it corrects that one instead. The amount stays
 * open only while none of the receipt has been set against a bill - after
 * that the figure is holding an invoice up, and the way back is to reverse
 * the receipt and take it again so both entries stay on the record.</p>
 */
export function RecordPaymentDialog({
  open,
  order,
  payment,
  customerId: openOn,
  onClose,
}: {
  open: boolean
  /** Prefills and locks the order when opened from an order screen. */
  order?: OrderDetail | null
  /** The receipt being corrected. Absent, a new one is taken. */
  payment?: Payment | null
  /** Opens on this customer, for money taken against their outstanding bills. */
  customerId?: string
  onClose: () => void
}) {
  const record = useRecordPayment()
  const update = useUpdatePayment()
  const editing = Boolean(payment)
  const hasRole = useAuthStore((state) => state.hasRole)
  const isAdmin = hasRole('SUPER_ADMIN') || hasRole('MANAGEMENT')
  const amountLocked = Boolean(payment && payment.allocatedAmount > 0)
  const { data: outlets } = useOutlets()
  const [customerSearch, setCustomerSearch] = useState('')
  const { data: customers } = useCustomers({ q: customerSearch || undefined, size: 20 })

  const [customerId, setCustomerId] = useState('')
  const [orderIds, setOrderIds] = useState<string[]>([])
  const [outletId, setOutletId] = useState('')
  const [paymentMode, setPaymentMode] = useState('UPI')
  const [paymentDate, setPaymentDate] = useState(() => isoDate(0))
  const [amount, setAmount] = useState('')
  const [bankReference, setBankReference] = useState('')
  const [instrumentNumber, setInstrumentNumber] = useState('')
  const [securityDeposit, setSecurityDeposit] = useState(false)
  const [remarks, setRemarks] = useState('')
  // Old data, entered as it stood - never for a receipt taken today, which
  // keeps its place in this company's own run of numbers. Admin only, and
  // only on a new receipt: once one exists its number is not retyped here.
  const [manualNumbering, setManualNumbering] = useState(false)
  const [receiptNumber, setReceiptNumber] = useState('')
  // What of this receipt goes against which bill, keyed by invoice. A bill not
  // in here is not being paid; one in here carries the figure being put to it,
  // which is its whole balance unless somebody types otherwise.
  const [settling, setSettling] = useState<Record<string, string>>({})

  // Orders the money can be applied to, once a customer is known. A page size
  // of zero is rejected by the API, so the idle query asks for one row.
  const { data: customerOrders } = useOrders(
    customerId && !order ? { customerId, size: 50 } : { size: 1 },
  )
  const { data: schedule } = usePaymentSchedule(order?.id ?? (orderIds.length === 1 ? orderIds[0] : undefined))

  // Every bill this customer still owes, whichever order it came from. A
  // customer with three events running settles all three in one payment, and
  // that is one receipt, not three.
  const { data: outstanding } = useOutstandingInvoices({
    customerId: customerId || undefined,
    enabled: Boolean(customerId) && !editing,
  })
  // Narrowed to the orders picked, or to the one order the dialog opened from.
  // Nothing picked means every bill the customer owes.
  const pickedOrders = order?.id ? [order.id] : orderIds
  const bills = (outstanding ?? []).filter(
    (bill) => pickedOrders.length === 0 || pickedOrders.includes(bill.orderId ?? ''),
  )
  const settled = Object.entries(settling)
    .filter(([invoiceId, value]) => Number(value) > 0 && bills.some((bill) => bill.id === invoiceId))
    .map(([invoiceId, value]) => ({
      invoiceId,
      orderId: bills.find((bill) => bill.id === invoiceId)?.orderId,
      amount: Number(value),
    }))
  const settledTotal = settled.reduce((sum, line) => sum + line.amount, 0)
  const overAllocated = settledTotal > Number(amount || 0) + 0.001

  // Orders with no invoice have no bills to settle, so with more than one order
  // picked the receipt offsets their value directly, in the order picked, up to
  // what is left of the amount once the bills are paid.
  const orderOffsets = (() => {
    if (orderIds.length < 2) return []
    let remaining = Number(amount || 0) - settledTotal
    return orderIds
      .filter((id) => !bills.some((bill) => bill.orderId === id))
      .flatMap((id) => {
        const balance = Number(customerOrders?.content?.find((c) => c.id === id)?.balanceAmount ?? 0)
        const take = Math.min(balance, Math.max(remaining, 0))
        remaining -= take
        return take > 0 ? [{ orderId: id, amount: take }] : []
      })
  })()
  const orderRows = (customerId ? (customerOrders?.content ?? []) : [])
    .filter((candidate) => Number(candidate.balanceAmount ?? 0) > 0)

  /** Puts these orders on the receipt: their bills are ticked for what is owed and the amount follows. */
  const pickOrders = (picked: string[]) => {
    setOrderIds(picked)
    const first = customerOrders?.content?.find((c) => c.id === picked[0])
    if (first) setOutletId(first.outletId ?? '')
    const owed = (outstanding ?? []).filter(
      (bill) => picked.includes(bill.orderId ?? '') && bill.balanceAmount > 0,
    )
    setSettling(Object.fromEntries(owed.map((bill) => [bill.id, String(bill.balanceAmount)])))
    // The amount is what the picked orders owe in total. Their bills and the
    // orders they have not yet been invoiced for make up that total, so the
    // two always agree.
    const orderBalance = (customerOrders?.content ?? [])
      .filter((candidate) => picked.includes(candidate.id))
      .reduce((sum, candidate) => sum + Number(candidate.balanceAmount ?? 0), 0)
    setAmount(picked.length > 0 ? String(orderBalance) : '')
  }

  /** Tick a bill for what is still owed on it, or take it off the receipt. */
  const toggleBill = (invoiceId: string, balance: number, on: boolean) =>
    setSettling((current) => {
      const next = { ...current }
      if (on) {
        next[invoiceId] = String(balance)
      } else {
        delete next[invoiceId]
      }
      // The amount follows what is being settled, so the common case - pay off
      // these bills - needs no arithmetic at the counter.
      const total = Object.values(next).reduce((sum, value) => sum + Number(value || 0), 0)
      if (total > 0) setAmount(String(total))
      return next
    })

  // A fresh key per opening; retrying the same submit stays idempotent.
  const [idempotencyKey, setIdempotencyKey] = useState(() => newId())

  const awaitingAdvance = order?.status === 'AWAITING_ADVANCE'
  const advanceOutstanding = order
    ? Math.max(0, (order.advanceRequired ?? 0) - (order.paidAmount ?? 0))
    : 0

  useEffect(() => {
    if (!open) return
    setIdempotencyKey(newId())
    setSettling({})
    setManualNumbering(false)
    setReceiptNumber('')
    if (payment) {
      setCustomerId(payment.customerId)
      setOrderIds(payment.orderId ? [payment.orderId] : [])
      setOutletId(payment.outletId ?? '')
      setPaymentMode(payment.paymentMode)
      setPaymentDate(payment.paymentDate)
      setAmount(String(payment.amount))
      setBankReference(payment.bankReference ?? '')
      setInstrumentNumber(payment.instrumentNumber ?? '')
      setSecurityDeposit(Boolean(payment.securityDeposit))
      setRemarks(payment.remarks ?? '')
      return
    }
    if (openOn) {
      setCustomerId(openOn)
    }
    if (order) {
      setCustomerId(order.customerId)
      setOrderIds([order.id])
      setOutletId(order.outletId ?? '')
      // Default to what actually unblocks the order.
      setAmount(String(awaitingAdvance ? advanceOutstanding : (order.balanceAmount ?? 0)))
      setRemarks(awaitingAdvance ? 'Booking advance' : '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.id, payment?.id, openOn])

  const nextMilestone = (schedule ?? []).find((milestone) => milestone.status !== 'PAID')
  const valid = Boolean(customerId && outletId && Number(amount) > 0) && !overAllocated

  const body = () => ({
    customerId,
    orderId: orderIds.length === 1 ? orderIds[0] : undefined,
    outletId,
    paymentDate,
    paymentMode,
    amount: Number(amount),
    securityDeposit,
    // Blanked deliberately, so a wrong cheque number can be cleared and not
    // merely written over.
    bankReference,
    instrumentNumber,
    remarks,
    // Named bills win over the automatic oldest-first allocation: the counter
    // has said what this money is for.
    allocations: [...settled, ...orderOffsets].length > 0 ? [...settled, ...orderOffsets] : undefined,
    receiptNumber:
      isAdmin && manualNumbering && receiptNumber.trim() ? receiptNumber.trim() : undefined,
  })

  const submit = () => {
    if (payment) {
      update.mutate({ id: payment.id, body: body() }, { onSuccess: onClose })
      return
    }
    record.mutate({ idempotencyKey, body: body() }, { onSuccess: onClose })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editing
          ? `Correct receipt ${payment?.receiptNumber}`
          : order
            ? `Collect payment — ${order.orderNumber}`
            : 'Record a payment'}
      </DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={record.error ?? update.error} />

        {editing ? (
          <Alert severity={amountLocked ? 'warning' : 'info'} sx={{ mb: 2 }}>
            {amountLocked
              ? `${formatMoney(payment?.allocatedAmount ?? 0)} of this receipt is already set against
                 bills, so the amount is fixed. To change it, reverse the receipt and take it again.`
              : 'Correcting the record of the money, not the money. The receipt number stays as it is.'}
          </Alert>
        ) : awaitingAdvance ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {formatMoney(advanceOutstanding)} of booking advance is outstanding. Recording it{' '}
            <strong>confirms the order</strong> and locks the hall.
          </Alert>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The receipt is allocated to the oldest outstanding milestone first. Sending the same request
            twice will not double-count the collection.
          </Typography>
        )}

        <Grid container spacing={2}>
          {!(order && !editing) && (
            <>
              <Grid item xs={12}>
                <TextField
                  label="Find customer"
                  value={customerSearch}
                  onChange={(event) => setCustomerSearch(event.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  label="Customer"
                  value={customerId}
                  onChange={(event) => {
                    setCustomerId(event.target.value)
                    setOrderIds([])
                    setSettling({})
                    setAmount('')
                  }}
                  fullWidth
                  required
                  disabled={editing}
                >
                  {customers?.content.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name} · {formatMobile(customer.mobile)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Against orders
                </Typography>
                {orderRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No order with a balance for this customer. The money will be held on account.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Order</TableCell>
                        <TableCell align="right">Balance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderRows.map((candidate) => {
                        const on = orderIds.includes(candidate.id)
                        return (
                          <TableRow
                            key={candidate.id}
                            hover
                            selected={on}
                            sx={{ cursor: 'pointer' }}
                            onClick={() =>
                              pickOrders(on
                                ? orderIds.filter((id) => id !== candidate.id)
                                : [...orderIds, candidate.id])
                            }
                          >
                            <TableCell padding="checkbox">
                              <Checkbox checked={on} />
                            </TableCell>
                            <TableCell>{candidate.orderNumber}</TableCell>
                            <TableCell align="right">{formatMoney(candidate.balanceAmount)}</TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                )}
              </Grid>
            </>
          )}

          {/* The bills this money is for. Shown as soon as a customer is known,
              because that is the question at the counter - what is outstanding -
              and it is answered before anybody types a figure.

              Several can be ticked: a customer with three events running hands
              over one payment for all three, and that is one receipt against
              three bills, not three receipts. */}
          {!editing && customerId ? (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Bills outstanding
              </Typography>
              {bills.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nothing outstanding{order ? ' on this order' : ' for this customer'}. The
                  money will be held on account.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Invoice</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Order</TableCell>
                      <TableCell align="right">Outstanding</TableCell>
                      <TableCell align="right" width={140}>
                        Paying now
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bills.map((bill) => {
                      const ticked = settling[bill.id] !== undefined
                      return (
                        <TableRow key={bill.id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={ticked}
                              onChange={(event) =>
                                toggleBill(bill.id, bill.balanceAmount, event.target.checked)
                              }
                              inputProps={{ 'aria-label': `Settle ${bill.invoiceNumber}` }}
                            />
                          </TableCell>
                          <TableCell>{bill.invoiceNumber}</TableCell>
                          <TableCell>{formatDate(bill.invoiceDate)}</TableCell>
                          <TableCell>{bill.orderNumber ?? '—'}</TableCell>
                          <TableCell align="right">{formatMoney(bill.balanceAmount)}</TableCell>
                          <TableCell align="right">
                            {/* Part payment is ordinary: a customer clears one
                                bill and puts something towards the next. */}
                            <TextField
                              size="small"
                              type="number"
                              disabled={!ticked}
                              value={settling[bill.id] ?? ''}
                              onChange={(event) => {
                                const next = { ...settling, [bill.id]: event.target.value }
                                setSettling(next)
                                // The amount is what the paying column adds up to.
                                setAmount(String(
                                  Object.values(next).reduce((sum, value) => sum + Number(value || 0), 0),
                                ))
                              }}
                              inputProps={{ min: 0, max: bill.balanceAmount, style: { textAlign: 'right' } }}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
              {settled.length > 0 ? (
                <Alert severity={overAllocated ? 'error' : 'info'} sx={{ mt: 1 }}>
                  {overAllocated
                    ? `${formatMoney(settledTotal)} is being set against bills, which is more than the
                       ${formatMoney(Number(amount || 0))} received.`
                    : `${formatMoney(settledTotal)} of this receipt settles ${settled.length} ${
                        settled.length === 1 ? 'bill' : 'bills'
                      }. Anything over that is held on the customer's account.`}
                </Alert>
              ) : null}
            </Grid>
          ) : null}

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Outlet"
              value={outletId}
              onChange={(event) => setOutletId(event.target.value)}
              fullWidth
              required
              disabled={Boolean(order)}
            >
              {outlets?.map((outlet) => (
                <MenuItem key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Mode"
              value={paymentMode}
              onChange={(event) => setPaymentMode(event.target.value)}
              fullWidth
            >
              {MODES.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* What the policy asks for, beside the box the figure is typed
              into. Read only: it is the business's own answer, not something
              the counter decides, and it was previously only in a sentence at
              the top that nobody reads twice. */}
          {order && !editing ? (
            <Grid item xs={12} md={6}>
              {/* Shown on every order, including the ones that ask for nothing:
                  a field that disappears is a field somebody reports missing,
                  and "none asked for" is an answer. */}
              <TextField
                label="Recommended advance"
                value={
                  (order.advanceRequired ?? 0) > 0 ? formatMoney(order.advanceRequired) : 'None'
                }
                fullWidth
                InputProps={{ readOnly: true }}
                helperText={
                  (order.advanceRequired ?? 0) <= 0
                    ? 'This order asks for no advance'
                    : (order.paidAmount ?? 0) > 0
                      ? `${formatMoney(order.paidAmount)} already received · ${formatMoney(
                          advanceOutstanding,
                        )} still to come`
                      : (order.advanceRequired ?? 0) >= (order.totalAmount ?? 0)
                        ? 'The whole of the order is due under the advance policy'
                        : 'From the advance policy, rounded to the nearest thousand'
                }
              />
            </Grid>
          ) : null}
          <Grid item xs={12} md={6}>
            <TextField
              label="Amount"
              type="number"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              fullWidth
              required
              disabled={amountLocked}
              helperText={
                amountLocked
                  ? 'Fixed: part of this receipt is settling a bill'
                  : nextMilestone
                  ? `Next milestone: ${humanise(nextMilestone.milestone)} ${formatMoney(
                      nextMilestone.amountDue - nextMilestone.amountPaid,
                    )}`
                  : undefined
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Payment date"
              type="date"
              value={paymentDate}
              onChange={(event) => setPaymentDate(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Bank reference"
              value={bankReference}
              onChange={(event) => setBankReference(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={paymentMode === 'CHEQUE' ? 'Cheque number' : 'Instrument number'}
              value={instrumentNumber}
              onChange={(event) => setInstrumentNumber(event.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={securityDeposit}
                  onChange={(event) => setSecurityDeposit(event.target.checked)}
                />
              }
              label="Refundable security deposit"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Remarks"
              value={remarks}
              onChange={(event) => setRemarks(event.target.value)}
              fullWidth
            />
          </Grid>

          {/* Old data, typed in as it stood - not a receipt taken today, which
              takes its number from this company's own run and no other. */}
          {isAdmin && !editing ? (
            <>
              <Grid item xs={12} md={manualNumbering ? 4 : 12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={manualNumbering}
                      onChange={(event) => {
                        setManualNumbering(event.target.checked)
                        if (!event.target.checked) setReceiptNumber('')
                      }}
                    />
                  }
                  label="Old data — enter the document number"
                />
              </Grid>
              {manualNumbering ? (
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Receipt number"
                    value={receiptNumber}
                    onChange={(event) => setReceiptNumber(event.target.value)}
                    helperText="Exactly as it stood before — this company's own numbering carries on from where it is, untouched"
                  />
                </Grid>
              ) : null}
            </>
          ) : null}
        </Grid>

        {paymentMode === 'CHEQUE' && !editing ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            A cheque is recorded as pending until it clears, so it will not confirm the order on its own.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || record.isPending || update.isPending}
          onClick={submit}
        >
          {editing ? 'Save correction' : awaitingAdvance ? 'Record advance and confirm' : 'Record'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
